import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { ThemeToggle } from "../sirius/ThemeProvider";
import { LanguageToggle } from "../i18n";
import { log } from "../lib/log";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Landing for the one-time link emailed by /api/auth/2fa/recover.
 *  Reads ?token=… from the URL, posts to verify endpoint, stores the
 *  returned JWT and redirects to /account. */
export default function Recover2FA() {
  const { token: existingToken } = useAuth();
  const navigate = useNavigate();
  // Initial check happens at render time to satisfy react-hooks/set-state-in-effect.
  const initialToken = new URLSearchParams(window.location.search).get("token");
  const [status, setStatus] = useState(initialToken ? "verifying" : "error");
  const [error, setError] = useState(initialToken ? "" : "Mangler gjenopprettings-token i lenken.");

  useEffect(() => {
    if (!initialToken) return;  // already in "error" state from initializer
    if (existingToken) {
      // Already logged in — just bounce to account.
      navigate("/account", { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/auth/2fa/recover/verify?token=${encodeURIComponent(initialToken)}`);
        const d = await r.json().catch(() => ({}));
        if (cancelled) return;
        if (!r.ok) throw new Error(d.detail || "Kunne ikke gjenopprette tilgang");
        try { localStorage.setItem("cardscan_jwt", d.access_token); }
        catch (e) { log.warn("[recover] localStorage failed:", e?.message); }
        setStatus("success");
        setTimeout(() => { window.location.href = "/account?recovered=1"; }, 1200);
      } catch (e) {
        if (cancelled) return;
        setStatus("error");
        setError(e.message || "Lenken er ugyldig eller utløpt.");
      }
    })();
    return () => { cancelled = true; };
  }, [existingToken, initialToken, navigate]);

  return (
    <div className="srs" style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }} data-testid="recover-root">
      <nav style={{ padding: "16px 28px", borderBottom: "1px solid var(--srs-line)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 12, textDecoration: "none", color: "var(--srs-ink)" }}>
          <span className="srs-logo-dot" />
          <span className="srs-display" style={{ fontSize: 24, fontWeight: 500 }}>EuroPilot</span>
        </Link>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <LanguageToggle />
          <ThemeToggle />
        </div>
      </nav>
      <main style={{ flex: 1, display: "grid", placeItems: "center", padding: "60px 28px" }}>
        <div className="srs-card" style={{ width: "100%", maxWidth: 420, padding: 40, borderRadius: 24, textAlign: "center" }}>
          {status === "verifying" && (
            <>
              <div className="srs-mono" style={{ fontSize: 10, letterSpacing: "0.3em", color: "var(--srs-ink-dim)", marginBottom: 14 }}>// GJENOPPRETTER…</div>
              <h1 className="srs-display" style={{ fontSize: 28, fontWeight: 500, margin: "0 0 12px 0" }}>Logger deg inn</h1>
              <p style={{ color: "var(--srs-ink-dim)", fontSize: 14 }}>Vent et øyeblikk…</p>
            </>
          )}
          {status === "success" && (
            <>
              <div style={{ fontSize: 48, marginBottom: 14 }}>✓</div>
              <h1 className="srs-display" style={{ fontSize: 28, fontWeight: 500, margin: "0 0 12px 0", color: "var(--srs-good)" }}>Tilgang gjenopprettet</h1>
              <p style={{ color: "var(--srs-ink-dim)", fontSize: 14 }}>Sender deg til kontosiden…</p>
            </>
          )}
          {status === "error" && (
            <>
              <div className="srs-mono" style={{ fontSize: 10, letterSpacing: "0.3em", color: "var(--srs-warn)", marginBottom: 14 }}>// FEIL</div>
              <h1 className="srs-display" style={{ fontSize: 26, fontWeight: 500, margin: "0 0 12px 0" }}>Kunne ikke gjenopprette tilgang</h1>
              <p style={{ color: "var(--srs-ink-dim)", fontSize: 14, marginBottom: 24 }} data-testid="recover-error">{error}</p>
              <Link to="/login" className="srs-cta" style={{ display: "inline-block" }}>Til innlogging →</Link>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
